/**
 * @fileoverview N8N Service for workflow management and data ingestion
 * Handles workflow creation, execution, and secure credential management
 */

/**
 * @typedef {Object} N8NCredential
 * @property {string} id - Credential ID
 * @property {string} name - Credential name
 * @property {string} type - Credential type (e.g., 'httpBasicAuth', 'apiKey')
 * @property {Object} data - Encrypted credential data
 */

/**
 * @typedef {Object} N8NWorkflow
 * @property {string} id - Workflow ID
 * @property {string} name - Workflow name
 * @property {string} description - Workflow description
 * @property {boolean} active - Whether workflow is active
 * @property {Object[]} nodes - Workflow nodes configuration
 * @property {Object[]} connections - Node connections
 */

/**
 * @typedef {Object} DataSource
 * @property {string} id - Data source ID
 * @property {string} name - Human-readable name
 * @property {string} type - Source type (bank, investment, etc.)
 * @property {string} url - Portal URL
 * @property {string} credentialType - Required credential type
 * @property {Object} config - Source-specific configuration
 */

// Configuration
const N8N_CONFIG = {
  baseUrl: process.env.REACT_APP_N8N_URL || 'http://localhost:5678',
  apiKey: process.env.REACT_APP_N8N_API_KEY || '',
  webhookUrl: process.env.REACT_APP_N8N_WEBHOOK_URL || 'http://localhost:5678/webhook',
  lambdaUrl: process.env.REACT_APP_LAMBDA_SCRAPER_URL || 'https://your-lambda-api-gateway-url.amazonaws.com/scrape'
};

// Data source definitions - Chilean Financial Institutions
export const DATA_SOURCES = {
  BANCO_BICE: {
    id: 'banco_bice',
    name: 'Banco Bice',
    type: 'bank',
    url: 'https://www.bice.cl/personas/banca-digital',
    credentialType: 'httpBasicAuth',
    lambdaFunction: 'banco-bice-scraper',
    config: {
      loginSelector: '#usuario',
      passwordSelector: '#password',
      loginButtonSelector: '.btn-login',
      accountsSelector: '.producto-resumen',
      transactionsSelector: '.movimiento-item'
    }
  },
  BANCO_ITAU: {
    id: 'banco_itau',
    name: 'Banco Itaú',
    type: 'bank', 
    url: 'https://www.itau.cl/personas/banca-digital',
    credentialType: 'httpBasicAuth',
    lambdaFunction: 'banco-itau-scraper',
    config: {
      loginSelector: '#usuario',
      passwordSelector: '#clave',
      loginButtonSelector: '#btn-ingresar'
    }
  },
  SURA_INVERSIONES: {
    id: 'sura_inversiones',
    name: 'Sura Inversiones',
    type: 'investment',
    url: 'https://www.sura.cl/inversion/acceso-clientes',
    credentialType: 'httpBasicAuth',
    lambdaFunction: 'sura-inversiones-scraper',
    config: {
      loginSelector: 'input[name="usuario"]',
      passwordSelector: 'input[name="password"]',
      loginButtonSelector: 'button[type="submit"]'
    }
  },
  BANCO_SANTANDER: {
    id: 'banco_santander',
    name: 'Banco Santander',
    type: 'bank',
    url: 'https://www.santander.cl/personas/banca-digital',
    credentialType: 'httpBasicAuth',
    lambdaFunction: 'banco-santander-scraper',
    config: {
      loginSelector: '#rut',
      passwordSelector: '#password',
      loginButtonSelector: '#btn-continuar'
    }
  }
};

/**
 * N8N Service Error Class
 */
export class N8NServiceError extends Error {
  constructor(message, code = 'N8N_ERROR') {
    super(message);
    this.name = 'N8NServiceError';
    this.code = code;
  }
}

/**
 * Makes API request to N8N instance
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @returns {Promise<Object>} API response
 */
async function makeN8NRequest(endpoint, options = {}) {
  const url = `${N8N_CONFIG.baseUrl}/api/v1${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-N8N-API-KEY': N8N_CONFIG.apiKey,
      ...options.headers
    }
  });

  if (!response.ok) {
    throw new N8NServiceError(
      `N8N API request failed: ${response.statusText}`,
      'API_REQUEST_FAILED'
    );
  }

  return response.json();
}

/**
 * Creates secure credentials in AWS Secrets Manager
 * @param {string} dataSourceId - Data source identifier
 * @param {string} entityId - Entity identifier
 * @param {Object} credentials - Credential data (username, password, etc.)
 * @returns {Promise<string>} Secret name in AWS Secrets Manager
 */
export async function createSecureCredentials(dataSourceId, entityId, credentials) {
  try {
    // Generate unique secret name
    const secretName = `financial-portal/${dataSourceId}/${entityId}/${Date.now()}`;
    
    // This would typically be handled by a backend API
    // For now, we'll return the secret name that should be created manually
    // In production, you'd call your backend API that has AWS credentials

    // TODO: Call backend API to create secret in AWS Secrets Manager
    // const response = await fetch('/api/credentials/create', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ 
    //     secretName, 
    //     credentialData: {
    //       username: credentials.username,
    //       password: credentials.password,
    //       createdAt: new Date().toISOString(),
    //       dataSource: dataSourceId,
    //       entityId: entityId
    //     }
    //   })
    // });

    console.log('Credential creation requested for secret:', secretName);
    console.log('Note: This should be created in AWS Secrets Manager via backend API');

    return secretName;
  } catch (error) {
    console.error('Error creating secure credentials:', error);
    throw new N8NServiceError(
      'Failed to create secure credentials',
      'CREDENTIAL_CREATION_FAILED'
    );
  }
}

/**
 * Creates a data ingestion workflow in N8N using AWS Lambda for secure scraping
 * @param {string} dataSourceId - Data source identifier
 * @param {string} credentialSecretName - AWS Secrets Manager secret name
 * @param {string} entityId - Target entity ID
 * @param {Object} schedule - Workflow schedule (cron format)
 * @returns {Promise<string>} Workflow ID
 */
export async function createDataIngestionWorkflow(dataSourceId, credentialSecretName, entityId, schedule) {
  const dataSource = DATA_SOURCES[dataSourceId.toUpperCase()];
  if (!dataSource) {
    throw new N8NServiceError('Invalid data source ID', 'INVALID_DATA_SOURCE');
  }

  try {
    const workflow = {
      name: `Financial Data Sync - ${dataSource.name} - Entity ${entityId}`,
      active: false, // Start inactive for testing
      nodes: [
        // 1. Schedule Trigger
        {
          id: 'schedule',
          name: 'Schedule Trigger',
          type: 'n8n-nodes-base.cron',
          typeVersion: 1,
          position: [240, 300],
          parameters: {
            rule: {
              interval: [
                {
                  field: schedule.field || 'hours',
                  value: schedule.value || 6
                }
              ]
            }
          }
        },
        // 2. Call Specific AWS Lambda Scraper
        {
          id: 'lambda_scraper',
          name: `${dataSource.name} Scraper`,
          type: 'n8n-nodes-base.httpRequest',
          typeVersion: 3,
          position: [460, 300],
          parameters: {
            url: `${N8N_CONFIG.lambdaUrl}/${dataSource.lambdaFunction}`,
            method: 'POST',
            sendHeaders: true,
            headerParameters: {
              parameters: [
                {
                  name: 'Content-Type',
                  value: 'application/json'
                },
                {
                  name: 'Authorization',
                  value: `Bearer ${process.env.REACT_APP_LAMBDA_API_KEY || ''}`
                }
              ]
            },
            sendBody: true,
            bodyParameters: {
              parameters: [
                {
                  name: 'entityId',
                  value: entityId
                },
                {
                  name: 'credentialSecretName',
                  value: credentialSecretName
                },
                {
                  name: 'config',
                  value: JSON.stringify(dataSource.config)
                }
              ]
            },
            options: {
              timeout: 180000, // 3 minutes timeout for scraping
              response: {
                response: {
                  neverError: true
                }
              }
            }
          }
        },
        // 3. Process Lambda Response
        {
          id: 'process_response',
          name: 'Process Scraper Response',
          type: 'n8n-nodes-base.code',
          typeVersion: 1,
          position: [680, 300],
          parameters: {
            jsCode: `
// Process the AWS Lambda response
const response = $input.first().json;

console.log('Lambda response status:', response.statusCode);
console.log('Lambda response body:', JSON.stringify(response.body, null, 2));

// Check if the lambda execution was successful
if (response.statusCode !== 200) {
  throw new Error(\`Lambda scraper failed with status \${response.statusCode}: \${response.body}\`);
}

// Parse the response body
let scrapedData;
try {
  scrapedData = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
} catch (error) {
  throw new Error(\`Failed to parse Lambda response: \${error.message}\`);
}

// Check if scraping was successful
if (!scrapedData.success) {
  throw new Error(\`Scraping failed: \${scrapedData.error?.message || 'Unknown error'}\`);
}

// Extract and validate the data
const { accounts = [], transactions = [], summary = {} } = scrapedData.data;

console.log(\`Scraping successful: \${accounts.length} accounts, \${transactions.length} transactions\`);

// Prepare data for webhook
const processedData = {
  success: true,
  data: {
    accounts: accounts.map(account => ({
      ...account,
      source: '${dataSource.id}',
      entityId: '${entityId}',
      lastSync: new Date().toISOString()
    })),
    transactions: transactions.map(transaction => ({
      ...transaction,
      source: '${dataSource.id}',
      entityId: '${entityId}',
      importedAt: new Date().toISOString()
    })),
    summary: {
      ...summary,
      source: '${dataSource.id}',
      entityId: '${entityId}',
      syncedAt: new Date().toISOString()
    },
    metadata: scrapedData.data.metadata
  }
};

return [processedData];
            `
          }
        },
        // 4. Send to Application Webhook
        {
          id: 'webhook',
          name: 'Send to App',
          type: 'n8n-nodes-base.httpRequest',
          typeVersion: 3,
          position: [900, 300],
          parameters: {
            url: `${N8N_CONFIG.webhookUrl}/financial-data/${entityId}`,
            method: 'POST',
            sendHeaders: true,
            headerParameters: {
              parameters: [
                {
                  name: 'Content-Type',
                  value: 'application/json'
                },
                {
                  name: 'X-Source',
                  value: 'n8n-financial-sync'
                }
              ]
            },
            sendBody: true,
            bodyParameters: {
              parameters: [
                {
                  name: 'data',
                  value: '={{ $json }}'
                }
              ]
            }
          }
        },
        // 5. Error Handler
        {
          id: 'error_handler',
          name: 'Error Handler',
          type: 'n8n-nodes-base.code',
          typeVersion: 1,
          position: [680, 500],
          parameters: {
            jsCode: `
// Handle errors and send notifications
const errorInfo = $input.first();
console.error('Workflow error:', errorInfo);

// Prepare error notification
const errorNotification = {
  status: 'error',
  message: errorInfo.error?.message || 'Unknown workflow error',
  source: '${dataSource.id}',
  entityId: '${entityId}',
  timestamp: new Date().toISOString(),
  workflowId: $workflow.id,
  executionId: $execution.id
};

// Send error to application webhook
const webhookUrl = '${N8N_CONFIG.webhookUrl}/financial-data/${entityId}/error';

return [{
  json: errorNotification,
  webhookUrl: webhookUrl
}];
            `
          }
        }
      ],
      connections: {
        'Schedule Trigger': {
          main: [
            [
              {
                node: 'AWS Lambda Scraper',
                type: 'main',
                index: 0
              }
            ]
          ]
        },
        'AWS Lambda Scraper': {
          main: [
            [
              {
                node: 'Process Scraper Response',
                type: 'main',
                index: 0
              }
            ]
          ]
        },
        'Process Scraper Response': {
          main: [
            [
              {
                node: 'Send to App',
                type: 'main',
                index: 0
              }
            ]
          ]
        }
      },
      settings: {
        saveDataErrorExecution: 'all',
        saveDataSuccessExecution: 'all',
        saveManualExecutions: true,
        errorWorkflow: {
          enabled: true
        }
      }
    };

    const result = await makeN8NRequest('/workflows', {
      method: 'POST',
      body: JSON.stringify(workflow)
    });

    return result.id;
  } catch (error) {
    console.error('Error creating N8N workflow:', error);
    throw new N8NServiceError(
      'Failed to create data ingestion workflow',
      'WORKFLOW_CREATION_FAILED'
    );
  }
}

/**
 * Activates an N8N workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<boolean>} Success status
 */
export async function activateWorkflow(workflowId) {
  try {
    await makeN8NRequest(`/workflows/${workflowId}/activate`, {
      method: 'POST'
    });
    return true;
  } catch (error) {
    console.error('Error activating workflow:', error);
    throw new N8NServiceError(
      'Failed to activate workflow',
      'WORKFLOW_ACTIVATION_FAILED'
    );
  }
}

/**
 * Gets workflow execution history
 * @param {string} workflowId - Workflow ID
 * @param {number} limit - Number of executions to fetch
 * @returns {Promise<Object[]>} Execution history
 */
export async function getWorkflowExecutions(workflowId, limit = 10) {
  try {
    const result = await makeN8NRequest(`/executions?workflowId=${workflowId}&limit=${limit}`);
    return result.data || [];
  } catch (error) {
    console.error('Error fetching executions:', error);
    throw new N8NServiceError(
      'Failed to fetch workflow executions',
      'EXECUTIONS_FETCH_FAILED'
    );
  }
}

/**
 * Tests workflow connection with credentials
 * @param {string} dataSourceId - Data source ID
 * @param {Object} credentials - Test credentials
 * @returns {Promise<Object>} Test result
 */
export async function testConnection(dataSourceId, credentials) {
  const dataSource = DATA_SOURCES[dataSourceId.toUpperCase()];
  if (!dataSource) {
    throw new N8NServiceError('Invalid data source ID', 'INVALID_DATA_SOURCE');
  }

  try {
    // Create a test workflow to verify credentials
    const testWorkflow = {
      name: `Test Connection - ${dataSource.name}`,
      active: false,
      nodes: [
        {
          id: 'test',
          name: 'Test Login',
          type: 'n8n-nodes-base.httpRequest',
          typeVersion: 3,
          position: [240, 300],
          parameters: {
            url: dataSource.url,
            method: 'GET',
            sendHeaders: true,
            headerParameters: {
              parameters: [
                {
                  name: 'User-Agent',
                  value: 'Mozilla/5.0 (compatible; DataIngestionBot/1.0)'
                }
              ]
            }
          }
        }
      ],
      connections: {}
    };

    // Execute test workflow
    const result = await makeN8NRequest('/workflows/test', {
      method: 'POST',
      body: JSON.stringify({
        workflow: testWorkflow,
        credentials: credentials
      })
    });

    return {
      success: true,
      message: 'Connection test successful',
      data: result
    };
  } catch (error) {
    console.error('Connection test failed:', error);
    return {
      success: false,
      message: error.message || 'Connection test failed',
      error: error
    };
  }
}

const n8nService = {
  createSecureCredentials,
  createDataIngestionWorkflow,
  activateWorkflow,
  getWorkflowExecutions,
  testConnection,
  DATA_SOURCES
};

export default n8nService; 